import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Save } from 'lucide-react';
import { ImageUpload } from '@/components/ImageUpload';

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const TIMES = ['morning','afternoon','evening','flexible'];
const SKILL_LEVELS = ['newbie','beginner','intermediate','advanced'];
const SET_OPTIONS = ['yes','no','sometimes'];
const MEETUP_TYPES = ['public','public_and_private'];
const GENDER_PREFS = ['no_preference','women_only','men_only','mixed'];
const DISTANCES = ['5','10','15','25','50','50+'];
const GENDERS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];
const LESSON_TYPES = ['group', 'private', 'online', 'in-person'];
const ALERTS = ['email', 'sms', 'manual'];

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name || '', last_name: user.last_name || '', display_name: user.display_name || '',
        phone: user.phone || '', gender: user.gender || '', zip_code: user.zip_code || '',
        bio: user.bio || '', profile_photo: user.profile_photo || '',
        skill_level: user.skill_level || 'beginner', has_mahjong_set: user.has_mahjong_set || 'no',
        meetup_type: user.meetup_type || 'public', gender_preference: user.gender_preference || 'no_preference',
        travel_distance: user.travel_distance || '10',
        days_available: user.days_available || [], day_times: user.day_times || {},
        time_preferences: user.time_preferences || [],
        alert_preferences: user.alert_preferences || ['email'],
        last_minute_alerts: user.last_minute_alerts || false,
        is_instructor: user.is_instructor || false, instructor_public: user.instructor_public || false,
        instructor_bio: user.instructor_bio || '', instructor_rate: user.instructor_rate || '',
        instructor_lesson_types: user.instructor_lesson_types || [],
        social_facebook: user.social_facebook || '', social_instagram: user.social_instagram || '',
        social_twitter: user.social_twitter || '', social_linkedin: user.social_linkedin || '',
        hide_from_search: !!user.hide_from_search,
        allow_messages_from_anyone: user.allow_messages_from_anyone !== false, // default true
      });
    }
  }, [user]);

  const updateForm = (k, v) => setForm(p => ({...p, [k]: v}));
  const toggleArray = (key, value) => {
    setForm(prev => ({...prev, [key]: prev[key]?.includes(value) ? prev[key].filter(v => v !== value) : [...(prev[key]||[]), value]}));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put('/profile', form);
      await refreshUser();
      toast.success('Profile updated!');
    } catch { toast.error('Failed to update profile'); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="profile-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>My Profile</h1>
          <p className="text-slate-500 mt-1">Manage your player information</p>
        </div>
        <Button onClick={handleSave} disabled={loading} className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] gap-2" data-testid="profile-save-btn">
          <Save className="w-4 h-4" />{loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Basic Info */}
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Basic Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-6">
              <ImageUpload value={form.profile_photo} onChange={v => updateForm('profile_photo', v)} label="Profile Photo" shape="circle" />
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label className="text-sm mb-1.5 block">First Name</Label><Input value={form.first_name||''} onChange={e=>updateForm('first_name',e.target.value)} className="rounded-xl" data-testid="profile-first-name" /></div>
                  <div><Label className="text-sm mb-1.5 block">Last Name</Label><Input value={form.last_name||''} onChange={e=>updateForm('last_name',e.target.value)} className="rounded-xl" data-testid="profile-last-name" /></div>
                </div>
                <div><Label className="text-sm mb-1.5 block">Display Name</Label><Input value={form.display_name||''} onChange={e=>updateForm('display_name',e.target.value)} className="rounded-xl" data-testid="profile-display-name" /></div>
              </div>
            </div>
            <div><Label className="text-sm mb-1.5 block">Phone</Label><Input value={form.phone||''} onChange={e=>updateForm('phone',e.target.value)} className="rounded-xl" data-testid="profile-phone" /></div>
            <div><Label className="text-sm mb-1.5 block">Zip Code</Label><Input value={form.zip_code||''} onChange={e=>updateForm('zip_code',e.target.value)} maxLength={5} className="rounded-xl max-w-xs" data-testid="profile-zip" /></div>
            <div>
              <Label className="text-sm mb-2 block">Gender</Label>
              <p className="text-xs text-slate-400 mb-2">We ask this only to help ensure everyone feels safe when finding players. Your input on how we can word this better is always welcome.</p>
              <div className="flex flex-wrap gap-2">
                {GENDERS.map(g=>(
                  <button key={g.value} type="button" onClick={()=>updateForm('gender',g.value)} className={`px-4 py-2 rounded-full text-sm border transition-all ${form.gender===g.value?'bg-[#56c6df] text-white border-[#56c6df]':'border-slate-200 hover:border-slate-300'}`} data-testid={`profile-gender-${g.value}`}>{g.label}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Bio / Introduction</Label>
              <p className="text-xs text-slate-400 mb-1.5">Tell other players a bit about yourself — how you got into Mahjong, what you enjoy about it, or anything you'd like to share.</p>
              <Textarea value={form.bio||''} onChange={e=>updateForm('bio',e.target.value)} rows={3} placeholder="e.g. I've been playing American Mahjong for 3 years and love casual games with friends..." className="rounded-xl" maxLength={500} data-testid="profile-bio" />
              <p className="text-xs text-slate-400 mt-1 text-right">{(form.bio||'').length}/500</p>
            </div>
          </CardContent>
        </Card>

        {/* Mahjong Profile */}
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Mahjong Profile</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm mb-2 block">Skill Level</Label>
              <div className="flex flex-wrap gap-2">
                {SKILL_LEVELS.map(s=>(
                  <button key={s} type="button" onClick={()=>updateForm('skill_level',s)} className={`px-4 py-2 rounded-full text-sm border transition-all capitalize ${form.skill_level===s?'bg-[#56dfb4] text-slate-900 border-[#56dfb4]':'border-slate-200 hover:border-slate-300'}`} data-testid={`profile-skill-${s}`}>{s}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Mahjong Set</Label>
              <div className="flex flex-wrap gap-2">
                {SET_OPTIONS.map(s=>(
                  <button key={s} type="button" onClick={()=>updateForm('has_mahjong_set',s)} className={`px-4 py-2 rounded-full text-sm border transition-all capitalize ${form.has_mahjong_set===s?'bg-[#56c6df] text-white border-[#56c6df]':'border-slate-200 hover:border-slate-300'}`}>{s}</button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preferences */}
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Meetup Preferences</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm mb-2 block">Meetup Type</Label>
              <div className="flex flex-wrap gap-2">
                {MEETUP_TYPES.map(s=>(
                  <button key={s} type="button" onClick={()=>updateForm('meetup_type',s)} className={`px-4 py-2 rounded-full text-sm border transition-all ${form.meetup_type===s?'bg-[#56dfb4] text-slate-900 border-[#56dfb4]':'border-slate-200 hover:border-slate-300'}`}>{s==='public'?'Public only':'Public & Private'}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Gender Preference for Playing Groups</Label>
              <div className="flex flex-wrap gap-2">
                {GENDER_PREFS.map(s=>(
                  <button key={s} type="button" onClick={()=>updateForm('gender_preference',s)} className={`px-4 py-2 rounded-full text-sm border transition-all capitalize ${form.gender_preference===s?'bg-[#df5681] text-white border-[#df5681]':'border-slate-200 hover:border-slate-300'}`}>{s.replace(/_/g,' ')}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Travel Distance</Label>
              <div className="flex flex-wrap gap-2">
                {DISTANCES.map(d=>(
                  <button key={d} type="button" onClick={()=>updateForm('travel_distance',d)} className={`px-4 py-2 rounded-full text-sm border transition-all ${form.travel_distance===d?'bg-[#b456df] text-white border-[#b456df]':'border-slate-200 hover:border-slate-300'}`}>{d==='50+'?'50+ mi':`${d} mi`}</button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Availability */}
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Availability</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-400">Select the days you're available, then pick specific times for each day.</p>
            {DAYS.map(d => {
              const selected = form.days_available?.includes(d);
              const dayTimes = form.day_times?.[d] || [];
              return (
                <div key={d} className={`rounded-xl border transition-all ${selected ? 'border-[#56c6df] bg-[#56c6df]/5' : 'border-slate-200'}`}>
                  <button type="button" onClick={() => {
                    toggleArray('days_available', d);
                    if (selected) updateForm('day_times', {...(form.day_times||{}), [d]: undefined});
                  }} className="w-full flex items-center gap-3 p-3 text-left">
                    <Checkbox checked={selected} className="pointer-events-none" />
                    <span className="text-sm font-medium capitalize">{d}</span>
                  </button>
                  {selected && (
                    <div className="px-3 pb-3 flex flex-wrap gap-1.5 ml-8">
                      {['morning','afternoon','evening','flexible'].map(t => (
                        <button key={t} type="button" onClick={() => {
                          const cur = form.day_times?.[d] || [];
                          const next = cur.includes(t) ? cur.filter(v=>v!==t) : [...cur, t];
                          updateForm('day_times', {...(form.day_times||{}), [d]: next});
                        }}
                          className={`px-2.5 py-1 rounded-full text-xs border transition-all capitalize ${dayTimes.includes(t)?'bg-[#56c6df] text-white border-[#56c6df]':'border-slate-200 hover:border-slate-300'}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Privacy & Visibility */}
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Privacy &amp; Visibility</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 hover:border-[#b456df] cursor-pointer transition-all">
              <Checkbox
                checked={!!form.hide_from_search}
                onCheckedChange={v => updateForm('hide_from_search', !!v)}
                className="mt-0.5"
                data-testid="profile-hide-from-search"
              />
              <div>
                <p className="font-medium text-sm">Hide me from Find Players searches</p>
                <p className="text-xs text-slate-500">You won't appear in other players' search results. You can still reach out to others yourself.</p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 hover:border-[#b456df] cursor-pointer transition-all">
              <Checkbox
                checked={form.allow_messages_from_anyone !== false}
                onCheckedChange={v => updateForm('allow_messages_from_anyone', !!v)}
                className="mt-0.5"
                data-testid="profile-allow-messages-from-anyone"
              />
              <div>
                <p className="font-medium text-sm">Allow messages from anyone</p>
                <p className="text-xs text-slate-500">When off, only people you've reached out to first can message you. Great for staying private.</p>
              </div>
            </label>
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Alert Preferences</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              {ALERTS.map(a=>(
                <label key={a} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${form.alert_preferences?.includes(a)?'border-[#56dfb4] bg-[#56dfb4]/5':'border-slate-200 hover:border-slate-300'}`}>
                  <Checkbox checked={form.alert_preferences?.includes(a)} onCheckedChange={()=>toggleArray('alert_preferences',a)} />
                  <span className="text-sm capitalize">{a === 'sms' ? 'Text/SMS alerts' : a === 'manual' ? "I'll check the site manually" : 'Email alerts'}</span>
                </label>
              ))}
            </div>
            <div className="mt-3 p-3 rounded-xl bg-[#56dfb4]/5 border border-[#56dfb4]/20">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={form.last_minute_alerts||false} onCheckedChange={v=>updateForm('last_minute_alerts',v)} className="mt-0.5" data-testid="profile-last-minute" />
                <div>
                  <p className="font-medium text-sm">Alert me for last-minute games</p>
                  <p className="text-xs text-slate-500">Get notified when a player near you needs someone for a last-minute game.</p>
                </div>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Instructor */}
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Instructor Profile</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border border-slate-200 hover:border-[#b456df] transition-all">
              <Checkbox checked={form.is_instructor||false} onCheckedChange={v=>updateForm('is_instructor',v)} className="mt-0.5" data-testid="profile-instructor-toggle" />
              <div>
                <p className="font-medium text-sm">I teach Mahjong</p>
                <p className="text-xs text-slate-500">Check this if you offer group or private lessons</p>
              </div>
            </label>
            {form.is_instructor && (
              <div className="space-y-3 pl-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.instructor_public||false} onCheckedChange={v=>updateForm('instructor_public',v)} data-testid="profile-instructor-public" />
                  <span className="text-sm">List me publicly as an instructor</span>
                </label>
                <div>
                  <Label className="text-xs mb-1 block">Bio</Label>
                  <Input value={form.instructor_bio||''} onChange={e=>updateForm('instructor_bio',e.target.value)} placeholder="e.g. Teaching beginners for 5 years..." className="rounded-xl text-sm" data-testid="profile-instructor-bio" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Rate</Label>
                  <Input value={form.instructor_rate||''} onChange={e=>updateForm('instructor_rate',e.target.value)} placeholder="e.g. $30/hr" className="rounded-xl text-sm max-w-xs" data-testid="profile-instructor-rate" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Lesson Types</Label>
                  <div className="flex flex-wrap gap-2">
                    {LESSON_TYPES.map(t=>(
                      <button key={t} type="button" onClick={()=>toggleArray('instructor_lesson_types',t)}
                        className={`px-3 py-1 rounded-full text-xs border transition-all capitalize ${form.instructor_lesson_types?.includes(t)?'bg-[#b456df] text-white border-[#b456df]':'border-slate-200 hover:border-slate-300'}`}>{t}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Social Media */}
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Social Media</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-400 -mt-1">Linking social accounts helps others verify your identity before meeting in person.</p>
            <Input value={form.social_facebook||''} onChange={e=>updateForm('social_facebook',e.target.value)} placeholder="Facebook profile URL" className="rounded-xl text-sm" data-testid="profile-social-facebook" />
            <Input value={form.social_instagram||''} onChange={e=>updateForm('social_instagram',e.target.value)} placeholder="Instagram handle" className="rounded-xl text-sm" data-testid="profile-social-instagram" />
            <Input value={form.social_twitter||''} onChange={e=>updateForm('social_twitter',e.target.value)} placeholder="X (Twitter) handle" className="rounded-xl text-sm" data-testid="profile-social-twitter" />
            <Input value={form.social_linkedin||''} onChange={e=>updateForm('social_linkedin',e.target.value)} placeholder="LinkedIn profile URL" className="rounded-xl text-sm" data-testid="profile-social-linkedin" />
          </CardContent>
        </Card>

        {/* Save button at bottom too */}
        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={loading} className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] gap-2 px-8" data-testid="profile-save-btn-bottom">
            <Save className="w-4 h-4" />{loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
